import { useSyncExternalStore } from 'react';

import type { UUID } from '@aline/common';
import type { Apollo } from 'src/gql/generated';

/**
 * What the playbook editor page is looking at: the rule open in the details
 * panel and the suggested edit the review bar is stopped on, for the chat
 * drawer to send with each prompt as the visible context's `playbookFocus`.
 *
 * A module store rather than React context because the drawer renders
 * outside the page's tree, and rather than the URL because the current stop
 * is deliberately kept out of the history. The page publishes on every
 * selection change and clears on unmount; the drawer subscribes.
 */
export interface PublishedPlaybookFocus {
  focus: Apollo.PlaybookFocusInput;
  /** The playbook the focus belongs to, so a reader can ignore one that lags the URL. */
  playbookId: UUID;
}

let published: PublishedPlaybookFocus | null = null;
const listeners = new Set<() => void>();

/** Publishing what is already published is a no-op, so a re-run effect changes nothing. */
export function publishPlaybookFocus(next: PublishedPlaybookFocus | null): void {
  if (getIsSameFocus(published, next)) return;
  published = next;
  listeners.forEach(listener => listener());
}

export function usePlaybookFocus(): PublishedPlaybookFocus | null {
  return useSyncExternalStore(subscribe, getSnapshot);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): PublishedPlaybookFocus | null {
  return published;
}

function getIsSameFocus(
  a: PublishedPlaybookFocus | null,
  b: PublishedPlaybookFocus | null
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.playbookId === b.playbookId &&
    a.focus.ruleId === b.focus.ruleId &&
    a.focus.stop?.part === b.focus.stop?.part &&
    a.focus.stop?.target.playbookId === b.focus.stop?.target.playbookId &&
    a.focus.stop?.target.categoryId === b.focus.stop?.target.categoryId &&
    a.focus.stop?.target.ruleId === b.focus.stop?.target.ruleId &&
    a.focus.stop?.target.positionId === b.focus.stop?.target.positionId
  );
}
